import { isCreditKind } from '@platform/domain';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import {
  Activity,
  Building2,
  FileText,
  Landmark,
  Mail,
  PenLine,
  Send,
  Settings2,
  UserRound,
} from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { Toolbar } from 'react-aria-components';
import { isApiError } from '../../api/client';
import { organizationQuery, salesDocumentQuery, taxTypesQuery } from '../../api/queries';
import type { PartyView, SalesDocumentDetail, TimelineEvent } from '../../api/types';
import { useAccess } from '../../app/access';
import type { Dictionary } from '../../i18n/en';
import { useI18n } from '../../i18n/I18nProvider';
import { UserText } from '../../ui/Bidi';
import { Button } from '../../ui/Button';
import { Callout, Skeleton } from '../../ui/Feedback';
import { Money, Page, useDocumentTitle } from '../../ui/Layout';
import { QueryError } from '../../ui/QueryError';
import { NotFoundPage } from '../../app/ErrorPages';
import { useDocumentActions } from './actions';
import { DeliverySection } from './detail/DeliverySection';
import { EtaActions, etaActionsBusy } from './detail/EtaActions';
import { EtaVerdict } from './detail/EtaVerdict';
import { JourneyTracker } from './detail/JourneyTracker';
import { RelatedDocuments } from './detail/RelatedDocuments';
import { DocumentTools } from './DocumentTools';
import styles from './Sales.module.css';
import { DocStatusBadge, LIVE_STATES, MockLabel, TaxStatusBadge, taxAxisOf } from './status';

/**
 * MF-INV-003, one document (BLOCK-1-REDESIGN §A.5): the header with its badges; the document's
 * tools in a toolbar under it; the four-stage document journey; for a document the Tax Authority
 * refused, its verdict field by field with the correction (MF-INV-009, WP-1b.2); the buyer's
 * e-mail copy (MF-INV-010, WP-1b.4); three
 * at-a-glance tiles; the document at full width; its properties and the Tax Authority below it;
 * the history at the end. No side panel.
 */
/**
 * The page/tab title by kind (sales-notes.md §6; Block 11, WP-11.4). A credit note reads «إشعار
 * الدائن CN-…» and a debit note «إشعار المدين DN-…» — NOT «فاتورة إلكترونية CN-…», which treated
 * every non-export kind as a domestic invoice. The export variant of a note keeps the note title
 * (the export distinction is in the kind label and the PDF, not the page title).
 */
function documentTitle(t: Dictionary, kind: string, number: string): string {
  switch (kind) {
    case 'ei':
      return t.detail.exportTitle(number);
    case 'c':
    case 'ec':
      return t.detail.noteCreditTitle(number);
    case 'd':
    case 'ed':
      return t.detail.noteDebitTitle(number);
    default:
      return t.detail.title(number);
  }
}

/** The document-kind label (the hero sub-line and the Properties «نوع المستند»), by kind. */
function kindLabel(t: Dictionary, kind: string): string {
  switch (kind) {
    case 'ei':
      return t.detail.kindExport;
    case 'c':
      return t.detail.kindCredit;
    case 'ec':
      return t.detail.kindExportCredit;
    case 'd':
      return t.detail.kindDebit;
    case 'ed':
      return t.detail.kindExportDebit;
    default:
      return t.detail.kindLocal;
  }
}

export function InvoiceDetailPage() {
  const { t } = useI18n();
  const { orgId = '', docId = '' } = useParams({ strict: false }) as {
    orgId?: string;
    docId?: string;
  };
  const actions = useDocumentActions(orgId);
  const access = useAccess(orgId);
  const org = useQuery(organizationQuery(orgId));
  const glanceId = useId();
  const q = useQuery({
    ...salesDocumentQuery(orgId, docId),
    refetchInterval: (query) => {
      const s = query.state.data?.eta?.state.status;
      const mail = query.state.data?.delivery;
      // Sending, the buyer's e-mail copy queued or going out (WP-1b.4), or a request or a reading at
      // the Tax Authority (Block 11): every 1.5 s. Valid, rejected or cancelled: ETA may still
      // report a change while a window runs, so the open page keeps checking at a slower pace (no
      // manual reload, 6.05).
      if (
        (s && LIVE_STATES.has(s)) ||
        mail?.status === 'queued' ||
        mail?.status === 'sending' ||
        etaActionsBusy(query.state.data?.eta)
      )
        return 1500;
      if (s === 'valid') return 4000;
      if ((s === 'rejected' || s === 'cancelled') && query.state.data?.eta?.watch?.ended === false)
        return 4000;
      return false;
    },
  });
  const d = q.data;
  const title = d ? documentTitle(t, d.document.kind, d.document.number ?? '') : t.sales.title;
  useDocumentTitle(title);

  if (q.isError) {
    if (isApiError(q.error) && q.error.status === 404) return <NotFoundPage />;
    return (
      <Page>
        <QueryError error={q.error} onRetry={() => void q.refetch()} />
      </Page>
    );
  }
  if (!d)
    return (
      <Page>
        <Skeleton height="12rem" />
      </Page>
    );

  const doc = d.document;
  const isDraft = doc.status === 'draft';
  const isIssued = !isDraft && doc.status !== 'discarded';
  // Block 11 (role-4 B-1): a credit note credits the buyer, so its due box reads «المستحق للمشتري /
  // Due to buyer» — the on-screen half of the PDF's due box (D-9, DEC-112) — in the tile, the
  // document summary and the properties. The amount is unchanged; only the caption flips.
  const isCredit = isCreditKind(doc.kind);
  const axis = taxAxisOf({
    status: doc.status,
    readiness: d.readiness?.readiness ?? null,
    etaStatus: d.eta?.state.status ?? null,
  });
  // The e-seal step (WP-1b.1): sign an issued, unsigned document, then send it — before any ETA state.
  const beforeSubmission = (d.eta?.state.status ?? 'not_submitted') === 'not_submitted';
  const canSign = doc.status === 'issued' && beforeSubmission && access.can('invoice.issue');
  const canSend = doc.status === 'signed' && beforeSubmission && access.can('invoice.submit');
  const target = {
    id: doc.id,
    number: doc.number,
    version: doc.version,
    receiverName: d.receiver?.name ?? null,
    cash: Boolean(d.receiver?.cash),
    totalAmount: d.totals?.totalAmount ?? null,
  };

  return (
    <Page>
      <section className={styles.hero}>
        <div className={styles.heroRow}>
          <div>
            <span className={styles.heroEyebrow}>
              <FileText size={14} aria-hidden="true" />
              <Link
                to="/orgs/$orgId/sales/invoices"
                params={{ orgId }}
                style={{ color: 'inherit' }}
              >
                {t.sales.title}
              </Link>
            </span>
            <h1 className={styles.heroTitle} data-testid="detail-title">
              {title}
            </h1>
            <p className={styles.heroSub} data-testid="detail-kind">
              {kindLabel(t, doc.kind)}
            </p>
            <div className={styles.headerBadges} data-testid="detail-badges">
              <DocStatusBadge value={doc.status} testId="detail-status" />
              {axis ? <TaxStatusBadge value={axis} testId="detail-tax-status" /> : null}
              {isIssued ? <MockLabel environment={d.eta?.state.environment} /> : null}
            </div>
          </div>
        </div>
      </section>

      {/* Every tool for the state, visible; only the buttons carry test IDs (D-2 v2). */}
      <Toolbar
        aria-label={t.sales.rowActions(doc.number ?? '')}
        className={styles.detailToolbar}
        data-testid="detail-actions"
      >
        {canSign ? (
          <Button
            size="md"
            variant="primary"
            icon={<PenLine size={16} aria-hidden="true" />}
            testId="detail-sign"
            onPress={() => actions.requestSign(target)}
          >
            {t.sales.sign}
          </Button>
        ) : null}
        {canSend ? (
          <Button
            size="md"
            variant="primary"
            icon={<Send size={16} aria-hidden="true" />}
            testId="detail-send"
            onPress={() => actions.requestSend(target)}
          >
            {t.sales.sendToEta}
          </Button>
        ) : null}
        <DocumentTools
          orgId={orgId}
          doc={{
            id: doc.id,
            kind: doc.kind,
            status: doc.status,
            number: doc.number,
            version: doc.version,
            readiness: d.readiness?.readiness ?? null,
            receiverName: d.receiver?.name ?? null,
            cash: Boolean(d.receiver?.cash),
            totalAmount: d.totals?.totalAmount ?? null,
          }}
          prefix="detail"
          actions={actions}
        />
      </Toolbar>

      {doc.copiedFrom ? (
        <Callout tone="info" role="note">
          <span data-testid="copied-banner">
            {t.detail.copiedFromBanner(doc.copiedFrom.number ?? '')}{' '}
            <Link
              to="/orgs/$orgId/sales/invoices/$docId"
              params={{ orgId, docId: doc.copiedFrom.id }}
            >
              {doc.copiedFrom.number}
            </Link>
          </span>
        </Callout>
      ) : null}
      {doc.status === 'discarded' ? (
        <Callout tone="warning" role="note">
          {t.sales.voidNote}
        </Callout>
      ) : null}
      {/* WP-1b.2: a refused invoice and its replacement point at each other. */}
      {doc.status === 'superseded' && doc.supersededBy ? (
        <Callout tone="warning" role="note">
          <span data-testid="superseded-banner">
            {t.detail.supersededBanner}{' '}
            <Link
              to="/orgs/$orgId/sales/invoices/$docId"
              params={{ orgId, docId: doc.supersededBy.id }}
              data-testid="superseded-by-link"
            >
              {doc.supersededBy.number}
            </Link>
          </span>
        </Callout>
      ) : null}
      {doc.supersedes ? (
        <Callout tone="info" role="note">
          <span data-testid="supersedes-banner">
            {t.detail.supersedesBanner}{' '}
            <Link
              to="/orgs/$orgId/sales/invoices/$docId"
              params={{ orgId, docId: doc.supersedes.id }}
              data-testid="supersedes-link"
            >
              {doc.supersedes.number}
            </Link>
          </span>
        </Callout>
      ) : null}

      <JourneyTracker
        status={doc.status}
        readiness={d.readiness?.readiness ?? null}
        etaStatus={d.eta?.state.status ?? null}
        environment={d.eta?.state.environment ?? org.data?.etaEnvironment}
        issuedAt={isIssued ? doc.dateTimeIssued : null}
        eta={d.eta}
      />

      {/* Block 11 (D-5, AC-T6): the Tax Authority actions, a full-width band under the journey. */}
      {isIssued ? <EtaActions orgId={orgId} detail={d} /> : null}

      {/* Block 11 (D-7, D-13, AC-T6, AC-T8): a note's reference documents, or an invoice's notes;
          a full-width band in the page flow, between the actions and the verdict. */}
      <RelatedDocuments orgId={orgId} detail={d} />

      <EtaVerdict orgId={orgId} detail={d} actions={actions} />

      <DeliverySection orgId={orgId} detail={d} />

      {/* At a glance: who, when, how much. The ETA status is in the badges and the journey. */}
      <section aria-labelledby={glanceId} className={styles.glance} data-testid="detail-strip">
        <h2 id={glanceId} className="visually-hidden">
          {t.detail.glance}
        </h2>
        <div className={`${styles.kpi} ${styles.toneInfo}`}>
          <span className={styles.kpiLabel}>{t.detail.buyer}</span>
          <span className={styles.latestTitle}>
            {d.receiver?.cash ? t.sales.cash : (d.receiver?.name ?? t.sales.noCustomer)}
          </span>
        </div>
        <div className={`${styles.kpi} ${styles.toneInfo}`}>
          <span className={styles.kpiLabel}>{t.detail.issueDate}</span>
          <span className={styles.latestTitle}>
            {doc.issueDate ? <DateOnly value={doc.issueDate} /> : '—'}
          </span>
        </div>
        <div className={`${styles.kpi} ${styles.toneInfo}`}>
          <span className={styles.kpiLabel}>
            {isCredit ? t.detail.amountDueCreditEgp : t.detail.amountDueEgp}
          </span>
          <span className={styles.kpiValue}>
            {/* RI-08: a deleted draft owes nothing; the computed amount would still show. */}
            {doc.status === 'discarded' ? (
              '—'
            ) : (
              <Money value={d.totals?.amountDue} currency="EGP" />
            )}
          </span>
        </div>
      </section>

      <section className={styles.card} id="section-document" data-testid="section-document">
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>
            <span className={styles.cardIcon} aria-hidden="true">
              <FileText size={16} />
            </span>
            {t.detail.sections.document}
          </h2>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.parties}>
            <PartyCard
              title={t.detail.seller}
              icon={<Building2 size={14} />}
              party={d.issuer}
              testId="seller"
            />
            <PartyCard
              title={t.detail.buyer}
              icon={<UserRound size={14} />}
              party={d.receiver}
              testId="buyer"
            />
          </div>
          <dl className={styles.metaGrid} data-testid="detail-meta">
            <Meta
              label={t.editor.number}
              value={
                <span className={styles.mono} data-testid="meta-number">
                  {doc.number ?? '—'}
                </span>
              }
            />
            <Meta label={t.detail.type} value={kindLabel(t, doc.kind)} />
            {doc.dateTimeIssued && !isDraft ? (
              <Meta
                label={t.detail.issuedAt}
                value={<DateTime value={doc.dateTimeIssued} />}
                testId="meta-issued-at"
              />
            ) : (
              <Meta
                label={t.detail.issueDate}
                value={
                  doc.issueDate ? (
                    <DateOnly value={doc.issueDate} />
                  ) : (
                    t.editor.settings.issueDateHint
                  )
                }
                testId="meta-issue-date"
              />
            )}
            {doc.dueDate ? (
              <Meta
                label={t.detail.dueDate}
                value={<DateOnly value={doc.dueDate} />}
                testId="meta-due"
              />
            ) : null}
            {doc.serviceDeliveryDate ? (
              <Meta
                label={t.detail.serviceDate}
                value={<DateOnly value={doc.serviceDeliveryDate} />}
                testId="meta-service"
              />
            ) : null}
            <Meta label={t.detail.createdAt} value={<DateTime value={doc.createdAt} />} />
          </dl>
          <LinesTable detail={d} />
          <div className={styles.summaryBox}>
            <Summary detail={d} />
          </div>
        </div>
      </section>

      {/* Below the document: its properties and, once issued, the Tax Authority. */}
      <div className={isIssued ? styles.pair : undefined}>
        <section className={styles.card} id="section-settings" data-testid="section-settings">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>
              <span className={styles.cardIcon} aria-hidden="true">
                <Settings2 size={16} />
              </span>
              {t.detail.sections.settings}
            </h2>
          </div>
          <div className={styles.cardBody}>
            <dl className={styles.kv}>
              <dt>{t.detail.type}</dt>
              <dd data-testid="settings-type">{kindLabel(t, doc.kind)}</dd>
              <dt>{isCredit ? t.detail.amountDueCreditEgp : t.detail.amountDueEgp}</dt>
              <dd>
                <Money value={d.totals?.amountDue} currency="EGP" testId="settings-amount-due" />
              </dd>
              <dt>{t.detail.currency}</dt>
              <dd data-testid="settings-currency">{doc.currency}</dd>
              {doc.currency !== 'EGP' ? (
                <>
                  <dt>{t.detail.exchangeRate}</dt>
                  <dd className={styles.mono} data-testid="settings-rate">
                    {doc.exchangeRate ?? '—'}
                  </dd>
                  <dt>
                    {isCredit
                      ? t.detail.amountDueCreditForeign(doc.currency)
                      : t.detail.amountDueForeign(doc.currency)}
                  </dt>
                  <dd>
                    <Money
                      value={d.totals?.amountDueForeign}
                      currency={doc.currency}
                      testId="settings-amount-due-foreign"
                    />
                  </dd>
                </>
              ) : null}
              <dt>{t.detail.activity}</dt>
              <dd data-testid="settings-activity">
                {doc.activity ? (
                  <>
                    {`${doc.activity.code} — `}
                    <UserText>{doc.activity.nameAr ?? doc.activity.nameEn}</UserText>
                  </>
                ) : (
                  (doc.activityCode ?? '—')
                )}
              </dd>
              <dt>{t.detail.branch}</dt>
              <dd data-testid="settings-branch">
                {doc.branch ? (
                  <>
                    <UserText>{doc.branch.name}</UserText>
                    {` (${doc.branch.etaBranchId})`}
                  </>
                ) : (
                  '—'
                )}
              </dd>
              {(
                [
                  ['purchaseOrderRef', t.editor.additional.poRef],
                  ['purchaseOrderDescription', t.editor.additional.poDesc],
                  ['salesOrderRef', t.editor.additional.soRef],
                  ['salesOrderDescription', t.editor.additional.soDesc],
                  ['proformaInvoiceNumber', t.editor.additional.proforma],
                ] as const
              )
                .filter(([k]) => doc[k])
                .map(([k, label]) => (
                  <div key={k} style={{ display: 'contents' }}>
                    <dt>{label}</dt>
                    <dd data-testid={`settings-${k}`}>
                      <UserText>{doc[k]}</UserText>
                    </dd>
                  </div>
                ))}
            </dl>
          </div>
        </section>

        {isIssued ? <EtaPanel detail={d} /> : null}
      </div>

      {/* The history: full width, last. */}
      <section className={styles.card} id="section-activity" data-testid="section-activity">
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>
            <span className={styles.cardIcon} aria-hidden="true">
              <Activity size={16} />
            </span>
            {t.detail.sections.activity}
          </h2>
        </div>
        <div className={styles.cardBody}>
          <Timeline
            events={d.events}
            environment={d.eta?.state.environment ?? null}
            orgId={orgId}
          />
        </div>
      </section>
      {actions.elements}
    </Page>
  );
}

function Meta({ label, value, testId }: { label: string; value: ReactNode; testId?: string }) {
  return (
    <div className={styles.meta} data-testid={testId}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function DateOnly({ value }: { value: string }) {
  const { formatDate } = useI18n();
  return <span data-value={value}>{formatDate(value)}</span>;
}

function DateTime({ value }: { value: string }) {
  const { formatDateTime } = useI18n();
  return <span data-value={value}>{formatDateTime(value)}</span>;
}

function PartyCard({
  title,
  icon,
  party,
  testId,
}: {
  title: string;
  icon: ReactNode;
  party: PartyView | null;
  testId: string;
}) {
  const { t } = useI18n();
  if (!party)
    return (
      <div className={styles.party} data-testid={testId}>
        <h3>
          {icon} {title}
        </h3>
        <p className={styles.subtle}>{t.sales.noCustomer}</p>
      </div>
    );
  const a = party.address;
  const idLabel =
    party.type === 'P'
      ? t.detail.nationalId
      : party.type === 'F'
        ? t.detail.foreignId
        : t.detail.taxId;
  return (
    <div className={styles.party} data-testid={testId}>
      <h3>
        {icon} {title}
      </h3>
      <p className={styles.partyName} data-testid={`${testId}-name`}>
        {party.cash ? t.sales.cash : <UserText>{party.name}</UserText>}
      </p>
      {party.id ? (
        <p>
          {idLabel}:{' '}
          <span className={styles.mono} data-testid={`${testId}-id`}>
            {party.id}
          </span>
        </p>
      ) : null}
      {party.branchName ? (
        <p>
          <UserText>{party.branchName}</UserText>
        </p>
      ) : null}
      {a ? (
        <p data-testid={`${testId}-address`}>
          <UserText>
            {a.buildingNumber} {a.street}، {a.regionCity}، {a.governate}، {a.country}
          </UserText>
        </p>
      ) : null}
      {/* The paragraph keeps the page direction; only the value is isolated left-to-right. */}
      {party.email ? (
        <p data-testid={`${testId}-email`}>
          <span className={styles.mono}>{party.email}</span>
        </p>
      ) : null}
      {party.phone ? (
        <p>
          <span className={styles.mono}>{party.phone}</span>
        </p>
      ) : null}
    </div>
  );
}

function LinesTable({ detail }: { detail: SalesDocumentDetail }) {
  const { t } = useI18n();
  const currency = detail.document.currency;
  return (
    <div className={styles.scrollX}>
      <table className={styles.lineTable} data-testid="detail-lines">
        <caption className="visually-hidden">{t.detail.lines}</caption>
        <thead>
          <tr>
            <th>#</th>
            <th>{t.editor.lines.item}</th>
            <th>{t.catalogue.unit}</th>
            <th className={styles.end}>{t.editor.lines.quantity}</th>
            <th className={styles.end}>{t.editor.lines.unitPrice(currency)}</th>
            <th className={styles.end}>{t.editor.lines.discount}</th>
            <th>{t.editor.lines.taxes}</th>
            <th className={styles.end}>{t.editor.lines.total}</th>
          </tr>
        </thead>
        <tbody>
          {detail.lines.map((l) => (
            <tr key={l.lineNo} data-testid={`detail-line-${l.lineNo}`}>
              <td>{l.lineNo}</td>
              <td>
                <div style={{ fontWeight: 600 }}>{l.description}</div>
                <div className={`${styles.subtle} ${styles.mono}`}>
                  {l.itemType} {l.itemCode}
                  {l.weightUnitType ? ` · ${l.weightQuantity} ${l.weightUnitType}` : ''}
                </div>
              </td>
              <td className={styles.mono}>{l.unitType}</td>
              <td className={styles.end}>
                <span className={styles.mono} data-value={l.quantity ?? ''}>
                  {l.quantity ?? '—'}
                </span>
              </td>
              <td className={styles.end}>
                <Money value={l.unitPrice} currency={currency} />
              </td>
              <td className={styles.end}>
                <Money value={l.discountAmount} currency="EGP" />
              </td>
              <td>
                {l.taxes.map((x) => (
                  <div key={x.taxType} className={styles.subtle}>
                    {x.taxType}/{x.subType} {x.rate}% = <Money value={x.amount} currency="EGP" />
                  </div>
                ))}
              </td>
              <td className={styles.end}>
                <Money value={l.total} currency="EGP" testId={`detail-line-${l.lineNo}-total`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Summary({ detail }: { detail: SalesDocumentDetail }) {
  const { t, pick } = useI18n();
  const types = useQuery(taxTypesQuery);
  // Block 11 (role-4 B-1): the document summary's due box flips for a credit note, like the PDF.
  const isCredit = isCreditKind(detail.document.kind);
  const totals = detail.totals;
  if (!totals) return <p className={styles.note}>{t.editor.totals.waiting}</p>;
  const taxMap = new Map(totals.taxTotals.map((x) => [x.taxType, x.amount]));
  for (const tt of ['T1', 'T4']) if (!taxMap.has(tt)) taxMap.set(tt, '0.00000');
  const taxRows = [...taxMap.entries()].sort(
    (a, b) => Number(a[0].slice(1)) - Number(b[0].slice(1)),
  );
  const label = (code: string) => {
    const tt = types.data?.find((x) => x.code === code);
    return `${tt ? pick(tt.nameAr, tt.nameEn) : code} (${code})`;
  };
  const row = (l: string, v: string, id: string, deducted = false) => (
    <div className={styles.totalRow} key={id}>
      <dt>
        {l}
        {deducted ? <span className={styles.deduct}>({t.editor.totals.deducted})</span> : null}
      </dt>
      <dd>
        <Money value={v} currency="EGP" testId={id} />
      </dd>
    </div>
  );
  return (
    <dl className={styles.totals} data-testid="detail-summary">
      {row(t.editor.totals.salesTotal, totals.totalSalesAmount, 'sum-sales')}
      {row(t.editor.totals.discount, totals.totalDiscountAmount, 'sum-discount', true)}
      {row(t.editor.totals.net, totals.netAmount, 'sum-net')}
      {taxRows.map(([code, amount]) => row(label(code), amount, `sum-tax-${code}`, code === 'T4'))}
      {row(
        t.editor.totals.itemsDiscount,
        totals.totalItemsDiscountAmount,
        'sum-items-discount',
        true,
      )}
      {row(t.editor.totals.extraDiscount, totals.extraDiscountAmount, 'sum-extra-discount', true)}
      <div className={styles.grand}>
        <span>{isCredit ? t.detail.amountDueCreditEgp : t.detail.amountDueEgp}</span>
        <strong>
          <Money value={totals.amountDue} currency="EGP" testId="sum-amount-due" />
        </strong>
        {detail.document.currency !== 'EGP' && totals.amountDueForeign ? (
          <span>
            {isCredit
              ? t.detail.amountDueCreditForeign(detail.document.currency)
              : t.detail.amountDueForeign(detail.document.currency)}
            :{' '}
            <Money
              value={totals.amountDueForeign}
              currency={detail.document.currency}
              testId="sum-amount-due-foreign"
            />
          </span>
        ) : null}
      </div>
    </dl>
  );
}

function EtaPanel({ detail }: { detail: SalesDocumentDetail }) {
  const { t, formatDateTime } = useI18n();
  const eta = detail.eta;
  if (!eta) return null;
  const s = eta.state;
  return (
    <section className={styles.card} data-testid="eta-panel">
      <div className={styles.cardHead}>
        <h2 className={styles.cardTitle}>
          <span className={styles.cardIcon} aria-hidden="true">
            <Landmark size={16} />
          </span>
          {t.detail.sections.eta}
        </h2>
        <MockLabel environment={s.environment} />
      </div>
      <div className={styles.cardBody}>
        <dl className={styles.kv}>
          <dt>{t.sales.columns.taxStatus}</dt>
          <dd>
            <TaxStatusBadge value={s.status} testId="eta-status" />
          </dd>
          {s.etaUuid ? (
            <>
              <dt>{t.detail.eta.uuid}</dt>
              <dd className={styles.mono} data-testid="eta-uuid">
                {s.etaUuid}
              </dd>
            </>
          ) : null}
          {s.longId ? (
            <>
              <dt>{t.detail.eta.longId}</dt>
              <dd className={styles.mono} data-testid="eta-long-id">
                {s.longId}
              </dd>
            </>
          ) : null}
          <dt>{t.detail.eta.environment}</dt>
          <dd data-testid="eta-environment">{s.environment ? t.env[s.environment] : '—'}</dd>
          {s.statusReason ? (
            <>
              <dt>{t.detail.eta.reason}</dt>
              <dd data-testid="eta-reason">{s.statusReason}</dd>
            </>
          ) : null}
        </dl>
        {s.status === 'valid' ? <p className={styles.note}>{t.detail.eta.watching}</p> : null}
        {eta.submissions.length ? (
          <>
            <p className={styles.sectionLabel} style={{ marginBlockStart: 'var(--space-3)' }}>
              {t.detail.eta.submissions}
            </p>
            <ul className={styles.issueList} data-testid="eta-submissions">
              {eta.submissions.map((sub) => (
                <li key={sub.id} className={styles.subtle}>
                  {formatDateTime(sub.createdAt)} ·{' '}
                  {t.detail.eta.submissionStatus[sub.status] ?? sub.status} ·{' '}
                  {t.detail.eta.attempts(sub.attempts)}
                  {sub.submissionUuid ? (
                    <span className={styles.mono}> · {sub.submissionUuid}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className={styles.note}>{t.detail.eta.notSent}</p>
        )}
      </div>
    </section>
  );
}

/** Events whose reason names the other document by its number (copy; WP-1b.2 correction). */
const NUMBERED_EVENTS: ReadonlySet<string> = new Set([
  'copied_from',
  'copied_to',
  'correction_created',
  'correction_started',
  'superseded',
  'supersedes',
]);

/** The buyer's e-mail copy (WP-1b.4): what follows the sentence, when anything does. */
const EMAIL_EXTRA: ReadonlySet<string> = new Set(['email_requested', 'email_failed']);

const EVENT_TONE: Record<string, string> = {
  email_sent: 'evSuccess',
  email_saved: 'evSuccess',
  email_failed: 'evWarning',
  email_withheld: 'evNeutral',
  eta_valid: 'evSuccess',
  eta_reconciled: 'evSuccess',
  issued: 'evSuccess',
  eta_invalid: 'evDanger',
  eta_rejected: 'evDanger',
  eta_submission_rejected: 'evDanger',
  discarded: 'evNeutral',
  superseded: 'evNeutral',
  eta_cancelled: 'evNeutral',
  eta_cancelled_final: 'evNeutral',
  eta_outcome_unknown: 'evWarning',
  eta_retry_scheduled: 'evWarning',
  eta_submission_requested: 'evWarning',
  eta_accepted_for_validation: 'evWarning',
  // Block 11: the state changes at the Tax Authority.
  eta_cancel_requested: 'evWarning',
  eta_cancel_done: 'evWarning',
  eta_cancel_confirmed: 'evNeutral',
  eta_cancel_refused: 'evDanger',
  eta_cancel_failed: 'evDanger',
  eta_cancel_unknown: 'evWarning',
  eta_cancel_unconfirmed: 'evWarning',
  eta_cancel_released: 'evNeutral',
  eta_cancel_release_refused: 'evWarning',
  eta_decline_rejection_requested: 'evWarning',
  eta_decline_rejection_refused: 'evDanger',
  eta_decline_rejection_failed: 'evDanger',
  eta_decline_rejection_unconfirmed: 'evWarning',
  eta_decline_rejection_confirmed: 'evSuccess',
  eta_cancel_declined: 'evSuccess',
  eta_rejection_declined: 'evSuccess',
  eta_reject_request_reported: 'evDanger',
  eta_cancel_request_reported: 'evWarning',
};

/** Block 11: the reason the user typed for a cancellation (their text, isolated by direction). */
const USER_REASON_EVENTS: ReadonlySet<string> = new Set(['eta_cancel_requested']);

/**
 * Block 11 (W3-05): a state-change refusal's reason is stored as `<internal code> (<ETA code|local>)`.
 * The history must not show our internal code: it reads as the band's sentence (plus the next step
 * where one is defined), and keeps only the Authority's own code, isolated left to right.
 */
const REFUSAL_EVENTS: ReadonlySet<string> = new Set([
  'eta_cancel_refused',
  'eta_decline_rejection_refused',
]);

export function Timeline({
  events,
  environment,
  orgId,
}: {
  events: TimelineEvent[];
  environment: string | null;
  orgId: string;
}) {
  const { t, formatDateTime } = useI18n();
  void orgId;
  return (
    <ol className={styles.timeline} data-testid="timeline">
      {events.map((e) => {
        let extra: string | null = null;
        // W3-05: a refusal reads as the band's sentence (+ next step), the Authority's code alone.
        let refusal: { sentence: string; code: string | null } | null = null;
        if (NUMBERED_EVENTS.has(e.event) && e.reason) {
          try {
            const r = JSON.parse(e.reason) as { number?: string };
            if (r.number) extra = r.number;
          } catch {
            extra = null;
          }
        } else if (REFUSAL_EVENTS.has(e.event) && e.reason) {
          const m = /^(.+?) \((.+)\)$/.exec(e.reason);
          const internal = (m?.[1] ?? e.reason).trim();
          const paren = m?.[2]?.trim() ?? null;
          const act = t.detail.actions;
          const base =
            act.outcome[internal] ?? act.refusal[internal] ?? act.outcome.eta_refused ?? e.reason;
          const step = act.nextStep[internal];
          refusal = {
            sentence: step ? `${base} ${step}` : base,
            // Keep the Authority's own code only; "local" means we refused before sending.
            code: paren && paren !== 'local' ? paren : null,
          };
        } else if (e.reason && e.event.startsWith('eta_')) extra = e.reason;
        // The address requested (LTR) or the transport's code (design §4.3).
        else if (e.reason && EMAIL_EXTRA.has(e.event)) extra = e.reason;
        const isEmail = e.event.startsWith('email_');
        const tone = EVENT_TONE[e.event] ?? '';
        return (
          <li
            key={e.seq}
            className={`${styles.event} ${tone ? styles[tone as keyof typeof styles] : ''}`}
            data-testid="timeline-event"
            data-event={e.event}
          >
            <div className={styles.eventTitle}>
              {isEmail ? <Mail size={13} aria-hidden="true" className={styles.eventIcon} /> : null}
              {t.detail.events[e.event] ?? e.event}
              {/* RIM-02: «·» and its spaces stay in the sentence's direction; the value alone is
                  isolated left to right (an address, a code or a number). A reason the user typed
                  (Block 11) is isolated by its own direction instead. */}
              {refusal ? (
                <>
                  {' · '}
                  {refusal.sentence}
                  {refusal.code ? (
                    <>
                      {' '}
                      <bdi dir="ltr" className={styles.mono} data-testid="timeline-eta-code">
                        {refusal.code}
                      </bdi>
                    </>
                  ) : null}
                </>
              ) : extra && USER_REASON_EVENTS.has(e.event) ? (
                <>
                  {' · '}
                  <UserText>{extra}</UserText>
                </>
              ) : extra ? (
                <>
                  {' · '}
                  <bdi dir="ltr" className={styles.mono}>
                    {extra}
                  </bdi>
                </>
              ) : null}
            </div>
            <div className={styles.eventMeta}>
              <span data-testid="timeline-actor">
                {e.actorType === 'user' ? (e.actorName ?? '—') : t.detail.actorSystem}
              </span>
              <span data-testid="timeline-time" data-value={e.at}>
                {formatDateTime(e.at)}
              </span>
              {e.event.startsWith('eta_') && environment ? (
                <MockLabel environment={environment as 'mock'} />
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
